import React from 'react'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core'
import { AuthService, assets } from '@pulseops/common'
import { pipe } from 'fp-ts/function'
import { Throwable, ZIO } from '@mxt/zio'
import PDTable from './PDTable'

type Props = {
  open: boolean
  onClose: () => void
  info: {
    contractNumber?: string | null
    mainDoc?: string | null
    subDoc?: string | null
    imageFile?: string | null
  }
}

const headCell = [
  { id: 1, label: 'SỐ HỢP ĐỒNG' },
  { id: 2, label: 'NHÓM TÀI LIỆU' },
  { id: 3, label: 'TÀI LIỆU' },
  { id: 4, label: 'CHỮ KÝ' }
]

const rowCell = [
  { id: 1, label: 'contractNumber' },
  { id: 2, label: 'mainDoc' },
  { id: 3, label: 'subDoc' },
  { id: 4, label: 'action' }
]

export default function PDSignatureDialog({ open, onClose, info }: Props) {
  const handleViewImage = () => {
    if (!info.imageFile) return

    pipe(
      ZIO.zipPar(AuthService.token, AuthService.getLoginType),
      ZIO.flatMap(([token, loginType]) =>
        ZIO.fromPromise(() =>
          fetch(info.imageFile!, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'X-Authen-Vendor': loginType
            }
          })
        )
      ),
      ZIO.flatMap((res) => {
        if (!res.ok) {
          return ZIO.fail(Throwable(`Fetch document failed with status ${res.status}`))
        }
        const contentType = res.headers.get('content-type') || 'application/pdf'
        return pipe(
          ZIO.fromPromise(() => res.blob()),
          ZIO.map((blob) => new Blob([blob], { type: contentType }))
        )
      }),
      ZIO.map((fileBlob: Blob) => {
        const blobUrl = window.URL.createObjectURL(fileBlob)
        window.open(blobUrl, '_blank')
      }),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          if (info.imageFile) {
            window.open(info.imageFile, '_blank')
          }
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const rows = [
    {
      contractNumber: info.contractNumber || '-',
      mainDoc: info.mainDoc || '-',
      subDoc: info.subDoc || '-',
      action: info.imageFile ? (
        <button style={styles.linkButton} onClick={handleViewImage}>
          Xem
        </button>
      ) : (
        <span style={{ color: '#98a2b3' }}>Xem</span>
      )
    }
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16 } }}>
      <DialogTitle disableTypography style={styles.titleWrapper}>
        <span style={styles.title}>Xem chữ ký</span>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <assets.OBCallOtherClose />
        </IconButton>
      </DialogTitle>

      <DialogContent style={styles.content}>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          containerStyle={styles.tableContainer}
          headRowStyle={{ backgroundColor: '#f8fafc' }}
          headCellStyle={styles.headerCell}
        />
      </DialogContent>

      <DialogActions style={styles.actions}>
        <button style={styles.closeButton} onClick={onClose}>
          Đóng
        </button>
      </DialogActions>
    </Dialog>
  )
}

const styles: Record<string, any> = {
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #f1f5f9'
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#101828'
  },
  content: {
    padding: '24px'
  },
  tableContainer: {
    border: '1px solid #eaecf0',
    borderRadius: 12,
    overflow: 'hidden'
  },
  headerCell: {
    fontSize: 12,
    fontWeight: 700,
    color: '#6a7282',
    padding: '12px 16px',
    borderBottom: '1px solid #eaecf0',
    letterSpacing: '0.05em'
  },
  bodyCell: {
    fontSize: 14,
    color: '#101828',
    padding: '14px 16px',
    borderBottom: 'none'
  },
  linkButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ED1B2E',
    fontWeight: 600,
    fontSize: 14,
    padding: 0,
    textDecoration: 'none'
  },
  actions: {
    padding: '0 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  closeButton: {
    backgroundColor: '#fff',
    color: '#101828',
    border: '1px solid #d1d5dc',
    borderRadius: 16,
    padding: '8px 24px',
    fontWeight: 600,
    fontSize: 14,
    outline: 'none'
  }
}
